class LiveSearch extends HTMLElement {
  constructor () {
    super()
    this.isOpen = false
    this.searchTimeout = null
    this.currentSearchTerm = ''
    this.handleClickOutside = this.handleClickOutside.bind(this)
  }

  connectedCallback () {
    this.trigger = document.querySelectorAll('[data-search-trigger]')
    this.closeBtn = this.querySelector('[data-search-close]')
    this.clearBtn = this.querySelector('[data-search-clear]')
    this.input = this.querySelector('[data-predictive-search-input]')
    this.form = this.querySelector('form')
    this.resultsContainer = this.querySelector('[data-predictive-search-results]')
    this.initialStateContainer = this.querySelector('[data-search-initial-state]')
    this.mobileDrawer = document.querySelector('mobile-drawer')
    this.hamburger = document.querySelector('[data-drawer-trigger]')
    this.menuLinks = document.querySelectorAll('.hd-MenuItem[data-mega-menu="true"]')
    this.searchInner = this.querySelector('.sr-Inner')

    if (!this.trigger || !this.input || !this.form) return

    // Bind event listeners
    this.trigger.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault()
        if (this.isOpen) {
          this.close()
        } else {
          if (this.mobileDrawer?.classList.contains('md-Drawer_Active')) {
            this.mobileDrawer.close()
          }
          this.open()
        }
      })
    })

    // Handle click outside
    document.addEventListener('click', this.handleClickOutside)

    // Handle hamburger click to close search
    this.hamburger?.addEventListener('click', () => {
      if (this.isOpen) {
        this.close()
      }
    })

    // Close search when mobile drawer opens
    if (this.mobileDrawer) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.target.classList.contains('md-Drawer_Active') && this.isOpen) {
            this.close()
          }
        })
      })

      observer.observe(this.mobileDrawer, {
        attributes: true,
        attributeFilter: ['class'],
      })
    }

    // Close search on menu hover
    this.menuLinks?.forEach(link => {
      link.addEventListener('mouseenter', () => {
        if (this.isOpen && window.innerWidth >= 990) { // Only on desktop
          this.close()
        }
      })
    })

    this.closeBtn?.addEventListener('click', (e) => {
      // Prevent all event propagation
      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation()

      // Clear input and show initial state
      this.clearInput()

      // Return false to ensure no other handlers run
      return false
    })

    // Clear button - just clear input and show initial results
    this.clearBtn?.addEventListener('click', (e) => {
      // Prevent all event propagation
      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation()

      // Clear input and show initial results
      this.clearInput()

      // Return false to ensure no other handlers run
      return false
    })

    // Handle all suggestion clicks through event delegation
    this.addEventListener('click', (e) => {
      const suggestionBtn = e.target.closest('[data-search-suggestion]')
      if (suggestionBtn) {
        e.preventDefault()
        e.stopPropagation()
        this.input.value = suggestionBtn.textContent.trim()
        this.currentSearchTerm = suggestionBtn.textContent.trim()
        this.fetchResults(this.currentSearchTerm)
        this.hideInitialState()
      }
    })

    // Input handling with debounce
    this.input.addEventListener('input', () => {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        const term = this.input.value.trim()

        // Only fetch if we have a term and it's different from current
        if (term && term !== this.currentSearchTerm) {
          this.currentSearchTerm = term
          this.fetchResults(term)
          this.hideInitialState()
        } else if (!term) {
          this.currentSearchTerm = ''
          this.showInitialState()
        }
      }, 300)
    })

    // Keyboard handling
    document.addEventListener('keyup', (e) => {
      if (!this.isOpen) return

      if (e.key === 'Escape') {
        this.close()
      } else if (e.key === 'Backspace' && this.input.value === '') {
        this.clearInput()
      }
    })
  }

  disconnectedCallback () {
    document.removeEventListener('click', this.handleClickOutside)
  }

  handleClickOutside (event) {
    // If search is not open, do nothing
    if (!this.isOpen) return

    // Check if click is outside search inner container and trigger
    const isClickInside = this.contains(event.target) || this.trigger.some(trigger => trigger.contains(event.target))
    if (!isClickInside) {
      this.close()
    }
  }

  clearInput () {
    this.input.value = ''
    this.currentSearchTerm = ''
    this.input.focus()
    this.showInitialState()
  }

  showInitialState () {
    if (this.initialStateContainer) {
      this.initialStateContainer.hidden = false
      this.resultsContainer.innerHTML = ''
      this.resultsContainer.hidden = true
    }
  }

  hideInitialState () {
    if (this.initialStateContainer) {
      this.initialStateContainer.hidden = true
    }
    this.resultsContainer.hidden = false
  }

  open () {
    if (this.isOpen) return

    this.classList.add('sr-Overlay_Active')
    document.documentElement.style.overflow = 'hidden'
    this.isOpen = true
    document.querySelector('.hd-HeaderInner').classList.add('hd-HeaderInner_SearchOpen')

    requestAnimationFrame(() => {
      this.input.focus()
    })

    this.showInitialState()
  }

  close () {
    if (!this.isOpen) return

    this.classList.remove('sr-Overlay_Active')
    document.documentElement.style.overflow = ''
    this.isOpen = false
    document.querySelector('.hd-HeaderInner').classList.remove('hd-HeaderInner_SearchOpen')
    setTimeout(() => {
      if (!this.isOpen) {
        this.input.value = ''
        this.currentSearchTerm = ''
        this.resultsContainer.innerHTML = ''
      }
    }, 300)
  }

  async fetchResults (searchTerm) {
    if (!this.resultsContainer || !searchTerm) return

    const url = new URL(`${window.location.origin}/search/suggest`)
    url.searchParams.set('q', searchTerm.trim())
    url.searchParams.set('resources[type]', 'product,article')
    url.searchParams.set('resources[limit]', '9')
    url.searchParams.set('section_id', 'ajax-search-results')

    try {
      const response = await fetch(url.toString())
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const html = await response.text()
      if (!html) {
        throw new Error('Empty response received')
      }

      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const resultsSection = doc.getElementById('shopify-section-ajax-search-results')

      if (!resultsSection) {
        throw new Error('Results section not found in response')
      }

      // Only update if the search term hasn't changed
      if (searchTerm === this.currentSearchTerm) {
        this.resultsContainer.innerHTML = resultsSection.innerHTML
      }
    } catch (error) {
      console.error('Search error:', { error, searchTerm, url: url.toString() })
    }
  }
}

customElements.define('live-search', LiveSearch)
